// Todas las keys de sesión que hay que limpiar al forzar el logout. JWTContext.logout()
// hoy solo limpia 3 (accessToken/user/icn); un usuario impersonando además tiene las dos
// keys de impersonación, que también hay que borrar para no dejar sesión residual.
const AUTH_STORAGE_KEYS = [
  'accessToken',
  'user',
  'icn',
  'impersonatorToken',
  'impersonatorUser',
] as const;

// Rutas públicas — de auth (login/activación) o fuera del árbol de AuthGuard (report-download,
// distribution-history/public, organization/charts) — donde un visitante sin sesión válida se
// sienta legítimamente: no redirigir desde acá evita loops de auth y no secuestra una página
// pública cuando el navegador trae un accessToken viejo/inválido en localStorage (AMARU-802).
const PUBLIC_AUTH_PATHS = [
  '/login',
  '/change-password',
  '/activate-user',
  '/report-download',
  '/distribution-history/public',
  '/organization/charts',
];

// Guard de re-entrada a nivel de módulo: un batch de N requests puede disparar N errores
// de auth; queremos un solo redirect, no N.
let loggingOut = false;

/**
 * Fuerza el cierre de sesión ante un rechazo de auth del backend (usuario desactivado
 * o token inválido). Limpia todas las keys de sesión y recarga la app en /login.
 *
 * El full reload (window.location.replace) reconstruye el Apollo client desde cero
 * — cache limpio, WS link recreado sin token — y reinicia el AuthProvider en estado
 * desautenticado. Así resolvemos logout + reset de cache + cierre del WS + redirect de
 * una sola vez, sin pelear con que el errorLink vive fuera del árbol de React (no puede
 * llamar hooks ni al logout del contexto).
 *
 * No importa nada de src/graphql ni de src/contexts: el eventEmitter de esos módulos
 * importa `client` de src/graphql, y este archivo es consumido por src/graphql/index.ts
 * → un import de vuelta crearía un ciclo.
 */
export const forceLogout = (): void => {
  if (loggingOut) return;

  // Chequear el path ANTES de tocar localStorage: estando en una ruta pública de auth no
  // hay sesión que cerrar, y borrar acá pisaría un token recién escrito por login() —
  // que setea accessToken y navega sin recargar, así que un error tardío de una operación
  // previa puede llegar cuando el token nuevo ya está guardado.
  const onPublicAuthPath = PUBLIC_AUTH_PATHS.some((path) =>
    window.location.pathname.startsWith(path),
  );
  if (onPublicAuthPath) return;

  loggingOut = true;
  AUTH_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));
  window.location.replace('/login');
};
